import { useCallback, useRef, useState } from 'react';

export interface RecordingControls {
  readonly recording: boolean;
  readonly start: () => Promise<void>;
  readonly stop: () => void;
}

/** Records the station canvas using MediaRecorder + canvas.captureStream.
 *  Zero SaaS. Zero CI. One click → WebM download. */
export function useRecordingMode(
  getCanvas: () => HTMLCanvasElement | null,
): RecordingControls {
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const start = useCallback(async () => {
    const canvas = getCanvas();
    if (!canvas || recorderRef.current) return;
    const stream = canvas.captureStream(60);
    const mimeCandidates = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
    ];
    const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m));
    if (!mimeType) return;

    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 12_000_000 });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `command-deck-${stamp}.webm`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4_000);
      recorderRef.current = null;
      setRecording(false);
    };
    recorder.start(250);
    recorderRef.current = recorder;
    setRecording(true);
  }, [getCanvas]);

  const stop = useCallback(() => {
    recorderRef.current?.stop();
  }, []);

  return { recording, start, stop };
}
